import React from 'react';
import { t } from '../translations';
import { useLanguage } from '../LanguageContext';
import ManageSubscriptionButton from './ManageSubscriptionButton';

const Sidebar = ({ 
  view, 
  handleNavClick, 
  inboxMatches, 
  dismissedInboxItems, 
  handleLogout,
  userProfile 
}) => {
  const { language, setLanguage, languages } = useLanguage(); 
  
  const unreadCount = inboxMatches ? inboxMatches.filter(i => !dismissedInboxItems.includes(i.id)).length : 0;

  const navItems = [
    { id: 'home', label: t(language, 'menu_home'), icon: '🏠' },
    { id: 'topics', label: t(language, 'menu_search'), icon: '🔍' },
    { id: 'inbox', label: t(language, 'menu_inbox'), icon: '📬', badge: unreadCount },
    { id: 'saved', label: t(language, 'menu_saved'), icon: '📚' },
    { id: 'watches', label: t(language, 'menu_watches'), icon: '⚙️' },
  ];

  return (
    <>
      {/* DESKTOP SIDEBAR (Vänster) */}
      <aside className="hidden md:flex flex-col fixed left-0 top-0 bottom-0 w-64 bg-[#F8FAFC] border-r border-slate-200 z-50">
        <div className="p-6 flex items-center h-20">
          <span className="text-3xl">🔬</span>
          <span className="ml-3 font-bold text-xl text-slate-800">Science Monitor</span>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-2 px-4">
            {navItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full flex items-center p-3 rounded-xl transition-colors min-h-[44px] ${
                    view === item.id 
                      ? 'bg-blue-600 text-white shadow-sm font-medium' 
                      : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                  }`}
                >
                  <span className="text-xl relative flex-shrink-0">
                    {item.icon}
                    {item.badge > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                        {item.badge}
                      </span>
                    )}
                  </span>
                  <span className="ml-4 text-base">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Desktop bottensektion */}
        <div className="p-4 border-t border-slate-200">
          {/* Prenumerationshanterare (visas enbart för betalande konton) */}
          <ManageSubscriptionButton userProfile={userProfile} />

          <div className="flex items-center p-3 mb-2 rounded-xl bg-slate-100/50 border border-slate-200 hover:bg-slate-100 transition-colors">
            <span className="text-xl flex-shrink-0">🌍</span>
            <select 
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="ml-3 w-full bg-transparent text-slate-700 text-base font-medium outline-none cursor-pointer appearance-none"
            >
              {languages?.map(lang => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>

          <button 
            onClick={handleLogout}
            className="w-full flex items-center p-3 text-slate-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-colors min-h-[44px]"
          >
            <span className="text-xl flex-shrink-0">🚪</span>
            <span className="ml-4 text-base">{t(language, 'menu_logout')}</span>
          </button>
        </div>
      </aside>

      {/* MOBILE NAV BAR (Botten) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 flex items-center h-[68px] pb-[env(safe-area-inset-bottom)] shadow-md w-full max-w-full box-border">
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => handleNavClick(item.id)}
            className={`flex flex-col items-center justify-center flex-1 min-w-0 h-full transition-colors ${
              view === item.id ? 'text-blue-600' : 'text-slate-400'
            }`}
          >
            <span className="text-[20px] relative mb-0.5 shrink-0 mt-1">
              {item.icon}
              {item.badge > 0 && (
                <span className="absolute -top-1.5 -right-2 bg-red-500 text-white text-[9px] font-bold px-1 min-w-[16px] h-[16px] rounded-full flex items-center justify-center border border-white">
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </span>
            <span className="text-[9px] font-medium tracking-tighter truncate w-full px-1 text-center">
              {item.label}
            </span>
          </button>
        ))}
      </nav>
    </>
  );
};

export default Sidebar;